import { Grid, FormControl, RadioGroup, Radio, FormControlLabel } from '@mui/material';
import { memo } from 'react';
import { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { ForeignStateVM } from 'shell/components/shared/foreign-state-list';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { AccordionGeneralityFormVM } from 'front-office/view-models/certificate/generality-form';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import ConsulateRelease from './consulate-release';
import MunicipalityRelease from './municipality-release';
import { DocumentRadioCertificate } from 'front-office/constants/document-radio-certificate';

interface ItalianForeignStateProps {
  isForeign?: DocumentRadioCertificate;
  onChange: (
    key: keyof AccordionGeneralityFormVM,
    value: AccordionGeneralityFormVM[keyof AccordionGeneralityFormVM]
  ) => void;
  foreignStateBirth?: ForeignStateVM | null;
  foreignMunicipality?: MunicipalityAnprVM | null;
  provinceOfBirth?: string;
  municipalityOfBirth?: MunicipalityAnprVM | null;
  locality?: string;
  onChangeValues: (values: AccordionGeneralityFormVM) => void;
  values?: AccordionGeneralityFormVM;
  errors?: FormikErrors<AccordionGeneralityFormVM>;
  touched?: FormikTouched<AccordionGeneralityFormVM>;
  isAuthentic?: boolean;
  disabled?: boolean;
}

const ItalianForeignStateDocumentComponent = ({
  isForeign,
  onChange,
  values = {},
  onChangeValues,
  errors,
  touched,
  isAuthentic,
  disabled,
}: ItalianForeignStateProps): React.ReactElement => {
  const formContext = useFormikContext<AccordionGeneralityFormVM>();

  return (
    <Grid
      item
      container
      spacing={0}
      columnSpacing={{ xs: 0, md: 1 }}
      rowSpacing={1}
      alignItems="center"
    >
      <Grid item xs={12} md={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Stato"
            id="isForeignDocument"
            value={isForeign || DocumentRadioCertificate.IdentityCard}
            onChange={(event) =>
              onChangeValues({
                isForeignDocument: event.target.value as DocumentRadioCertificate,
                consulateCode: '',
                witness1: '',
                witness1IdentityCard: '',
                witness2: '',
                witness2IdentityCard: '',
                numberDocument: '',
                dateReleaseDocument: null,
                provinceDocument: '',
                municipalityDocument: null,
                localityDocument: '',
              })
            }
          >
            <FormControlLabel
              // onClick={() => {
              //   resetItalian();
              // }}
              value={DocumentRadioCertificate.IdentityCard}
              control={<Radio />}
              disabled={disabled}
              label="Carta d'identità"
            />
            <FormControlLabel
              // onClick={() => {
              //   resetForeign();
              // }}
              value={DocumentRadioCertificate.Other}
              disabled={disabled}
              control={<Radio />}
              label="Altro"
            />
            {isAuthentic ? (
              <FormControlLabel
                // onClick={() => {
                //   resetWitness();
                // }}
                value={DocumentRadioCertificate.Witness}
                disabled={disabled}
                control={<Radio />}
                label="Testimoni"
              />
            ) : undefined}
          </RadioGroup>
        </FormControl>
      </Grid>
      {isForeign !== DocumentRadioCertificate.Witness ? (
        <>
          <Grid item xs={12} md={6}>
            <PmiTextField
              label="Numero *"
              aria-label="Numero"
              fullWidth
              disabled={disabled}
              id="numberDocument"
              inputProps={{
                maxLength: '50',
              }}
              onChange={(event) => {
                formContext.setFieldTouched('numberDocument');
                onChangeValues({ numberDocument: event?.target?.value, isFromAnpr: false });
              }}
              value={
                values?.isFromAnpr &&
                values?.numberDocument &&
                !formContext?.touched?.numberDocument &&
                isForeign === DocumentRadioCertificate.IdentityCard
                  ? `Carta d'identità n.${values?.numberDocument}`
                  : values?.numberDocument || ''
              }
              error={touched?.numberDocument && Boolean(errors?.numberDocument)}
              helperText={errors?.numberDocument}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Data rilascio *"
              aria-label="Data rilascio"
              disabled={disabled}
              value={values?.dateReleaseDocument || null}
              onChange={(date) => {
                onChange('dateReleaseDocument', date);
              }}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateReleaseDocument"
                  fullWidth
                  error={touched?.dateReleaseDocument && Boolean(errors?.dateReleaseDocument)}
                  helperText=""
                />
              )}
            />
          </Grid>
        </>
      ) : undefined}
      {values?.isForeignDocument === DocumentRadioCertificate.Other ? (
        <ConsulateRelease
          values={values}
          disabled={disabled}
          onChange={onChange}
          isEditMode={!disabled}
          errors={errors}
          touched={touched}
        />
      ) : values?.isForeignDocument === DocumentRadioCertificate.IdentityCard ? (
        <MunicipalityRelease
          values={values}
          onChange={onChange}
          isEditMode={!disabled}
          disabled={disabled}
          errors={errors}
          touched={touched}
        />
      ) : (
        <>
          <Grid item xs={12}>
            <PmiTextField
              label="Testimone 1"
              aria-label="Testimone 1"
              fullWidth
              inputProps={{
                maxLength: '100',
              }}
              disabled={disabled}
              id="witness1"
              onChange={(event) => {
                onChange('witness1', event?.target?.value);
              }}
              value={values.witness1 || ''}
              error={touched?.witness1 && Boolean(errors?.witness1)}
              helperText={errors?.witness1}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label="Documento identità testimone 1"
              aria-label="Documento identità testimone 1"
              disabled={disabled}
              fullWidth
              id="witness1IdentityCard"
              onChange={(event) => {
                onChange('witness1IdentityCard', event?.target?.value);
              }}
              inputProps={{
                maxLength: '100',
              }}
              value={values.witness1IdentityCard || ''}
              error={touched?.witness1IdentityCard && Boolean(errors?.witness1IdentityCard)}
              helperText={errors?.witness1IdentityCard}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label="Testimone 2"
              aria-label="Testimone 2"
              disabled={disabled}
              fullWidth
              id="witness2"
              inputProps={{
                maxLength: '100',
              }}
              onChange={(event) => {
                onChange('witness2', event?.target?.value);
              }}
              value={values.witness2 || ''}
              error={touched?.witness2 && Boolean(errors?.witness2)}
              helperText={errors?.witness2}
            />
          </Grid>
          <Grid item xs={12}>
            <PmiTextField
              label="Documento identità testimone 2"
              aria-label="Documento identità testimone 2"
              disabled={disabled}
              fullWidth
              inputProps={{
                maxLength: '100',
              }}
              id="witness2IdentityCard"
              onChange={(event) => {
                onChange('witness2IdentityCard', event?.target?.value);
              }}
              value={values.witness2IdentityCard || ''}
              error={touched?.witness2IdentityCard && Boolean(errors?.witness2IdentityCard)}
              helperText={errors?.witness2IdentityCard}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

ItalianForeignStateDocumentComponent.displayName =
  'Filters Person section Italian or Foreign State component';

const ItalianForeignStateDocument = memo(ItalianForeignStateDocumentComponent);

export default ItalianForeignStateDocument as typeof ItalianForeignStateDocumentComponent;
